import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "../../components/Select";
import Ic from "../../components/icons.jsx";
import { CountryMultiSelect } from "../../components/CountryMultiSelect";
import type { PeriodPreset } from "./usePeriod";

/**
 * Redosled spiska kupaca.
 *
 * Spisak do sada nije imao nikakav redosled: baza je vraćala redove kako joj je zgodno, pa se
 * isti kupac mogao pojaviti na dve strane. Zato ovo nije samo izbor nego i uslov da paginacija
 * uopšte ima smisla.
 */
export const REDOSLEDI = [
  "DUG_OPADAJUCE", "DUG_RASTUCE", "DOSPELO_OPADAJUCE",
  "PROMET_OPADAJUCE", "UPLATA_NAJSTARIJA", "IME_AZ", "IME_ZA",
] as const;
export type Redosled = (typeof REDOSLEDI)[number];

export type FiltersValue = {
  preset: PeriodPreset;
  from: string;
  to: string;
  marketId?: string;
  /** Šifre država; prazno znači sve. Više njih odjednom, jer se tržišta porede.  */
  countries?: string[];
  /** Šifra valute; prazno znači sve valute, preračunato u evre. */
  currency?: string;
  q?: string;
};

export function FinanceFilters({
  value,
  markets,
  countries,
  currencies = [],
  searchPlaceholder,
  allowAll = false,
  sort,
  onSortChange,
  sporna = false,
  onSpornaChange,
  onChange,
}: {
  value: FiltersValue;
  markets: { id: string; name: string }[];
  countries: string[];
  /** Prazno na ekranima koji već stoje u jednoj valuti (stranica kupca, stranica dobavljača). */
  currencies?: string[];
  /**
   * Natpis u polju za pretragu.
   *
   * Stoji kao parametar jer isti filter služi i kupcima i dobavljačima, a polje je na ekranu
   * dobavljača pisalo „Kupac". Podrazumevano je kupac, pošto je filter tamo i nastao.
   */
  searchPlaceholder?: string;
  /**
   * Nudi i period „sve", koji briše opseg.
   *
   * Stoji kao izbor a ne kao podrazumevano: na finansijama period je deo pitanja („koliko duguje
   * ove godine"), a na registru je samo prozor — pa registar koji se otvara na godinu skraćuje i
   * pretragu nad svim podacima.
   */
  allowAll?: boolean;
  /**
   * Redosled spiska; stoji samo tamo gde spisak i postoji.
   *
   * Stranica jednog kupca nema šta da ređa, pa se izbor tamo i ne prikazuje: prazan padajući
   * spisak je obećanje koje ekran ne može da ispuni.
   */
  sort?: Redosled;
  onSortChange?: (v: Redosled) => void;
  /**
   * Sporna potraživanja: podrazumevano se ne prikazuju i ne ulaze u zbirove. Izbor stoji samo na ekranima kupaca
   * za zaposlene; oznaka je Bankomova, pa je kupac i dobavljač ne vide.
   */
  sporna?: boolean;
  onSpornaChange?: (v: boolean) => void;
  onChange: (v: FiltersValue) => void;
}) {
  const { t } = useTranslation();
  const presetOptions = ([
    ...(allowAll ? (["all"] as PeriodPreset[]) : []),
    "thisMonth", "lastMonth", "thisQuarter", "ytd", "last12m", "custom",
  ] as PeriodPreset[])
    .map((p) => ({ value: p, label: t(`finance.period.${p}`, p) }));

  return (
    <div style={bar}>
      <SearchBox
        value={value.q ?? ""}
        placeholder={searchPlaceholder ?? t("finance.filter.searchPlaceholder", "Kupac, PIB ili matični broj…")}
        ariaLabel={t("finance.filter.search", "Pretraga")}
        onChange={(q) => onChange({ ...value, q: q || undefined })}
      />
      {markets.length > 0 && (
        <Select
          value={value.marketId ?? ""}
          ariaLabel={t("finance.filter.market", "Market")}
          options={[{ value: "", label: t("finance.filter.allMarkets", "All markets") },
            ...markets.map((m) => ({ value: m.id, label: m.name }))]}
          onChange={(marketId) => onChange({ ...value, marketId: marketId || undefined })}
          style={{ minWidth: 160 }}
        />
      )}
      {countries.length > 0 && (
        <CountryMultiSelect
          codes={countries}
          value={value.countries ?? []}
          allLabel={t("finance.filter.allCountries", "Sve države")}
          ariaLabel={t("finance.filter.country", "Država")}
          onChange={(c) => onChange({ ...value, countries: c.length ? c : undefined })}
          style={{ minWidth: 170 }}
        />
      )}
      {currencies.length > 1 && (
        <Select
          value={value.currency ?? ""}
          ariaLabel={t("finance.filter.currency", "Valuta")}
          options={[
            // „Sve valute" znači preračun u evre, i tako i piše: skriven preračun u zaglavlju je
            // ono zbog čega se dinarski dug čini deviznim.
            { value: "", label: t("finance.filter.allCurrencies", "Sve valute (u €)") },
            ...currencies.map((c) => ({ value: c, label: c })),
          ]}
          onChange={(currency) => onChange({ ...value, currency: currency || undefined })}
          style={{ minWidth: 150 }}
        />
      )}
      <Select
        value={value.preset}
        ariaLabel={t("finance.period.label", "Period")}
        options={presetOptions}
        onChange={(preset) => onChange({ ...value, preset: preset as PeriodPreset })}
        style={{ minWidth: 150 }}
      />
      {value.preset === "custom" && (
        <>
          <input type="date" value={value.from} onChange={(e) => onChange({ ...value, from: e.target.value })} className="input" />
          <input type="date" value={value.to} onChange={(e) => onChange({ ...value, to: e.target.value })} className="input" />
        </>
      )}
      {onSpornaChange && (
        <Select
          value={sporna ? "1" : ""}
          ariaLabel={t("disputes.filter.label", "Disputed receivables")}
          options={[
            { value: "", label: t("disputes.filter.hide", "Don't show disputed") },
            { value: "1", label: t("disputes.filter.show", "Show disputed") },
          ]}
          onChange={(v) => onSpornaChange(v === "1")}
          style={{ minWidth: 180 }}
        />
      )}
      {onSortChange && (
        <Select
          value={sort ?? "DUG_OPADAJUCE"}
          ariaLabel={t("finance.sort.label", "Redosled")}
          // Ikonica nosi značenje: pored levka koji sužava, dve strelice kažu da se ovde ređa,
          // a ne bira šta se vidi.
          options={REDOSLEDI.map((r) => ({ value: r, label: sortLabel(r, t), left: <Ic.sort width={14} height={14} /> }))}
          onChange={(r) => onSortChange(r as Redosled)}
          style={{ minWidth: 190 }}
        />
      )}
    </div>
  );
}

/** Šta se kojim redosledom vidi; naziv kaže i po čemu i u kom smeru. */
function sortLabel(r: Redosled, t: (k: string, d: string) => string): string {
  switch (r) {
    case "DUG_OPADAJUCE":     return t("finance.sort.debtDesc", "Dug, najveći prvo");
    case "DUG_RASTUCE":       return t("finance.sort.debtAsc", "Dug, najmanji prvo");
    case "DOSPELO_OPADAJUCE": return t("finance.sort.overdueDesc", "Dospelo, najveće prvo");
    case "PROMET_OPADAJUCE":  return t("finance.sort.turnoverDesc", "Promet, najveći prvo");
    case "UPLATA_NAJSTARIJA": return t("finance.sort.oldestPayment", "Najduže bez uplate");
    case "IME_AZ":            return t("finance.sort.nameAz", "Ime, A\u2013Z");
    case "IME_ZA":            return t("finance.sort.nameZa", "Ime, Z\u2013A");
  }
}

/**
 * Pretraga sa odloženim slanjem.
 *
 * Spisak se učitava sa servera, pa svaki pritisnut taster inače znači jedan upit nad celom
 * bazom računa. Polje zato drži svoj tekst, a filter se menja tek kad se kucanje smiri.
 */
function SearchBox({
  value, placeholder, ariaLabel, onChange,
}: { value: string; placeholder: string; ariaLabel: string; onChange: (v: string) => void }) {
  const [tekst, setTekst] = useState(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Spolja se tekst menja samo kad se filter obriše negde drugde; tada polje mora da ga prati.
  useEffect(() => { setTekst(value); }, [value]);

  useEffect(() => {
    if (tekst === value) return;
    const id = setTimeout(() => onChangeRef.current(tekst.trim()), 300);
    return () => clearTimeout(id);
  }, [tekst, value]);

  return (
    <div style={{ position: "relative" }}>
      <span style={ikona}><Ic.search /></span>
      <input
        value={tekst}
        onChange={(e) => setTekst(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className="input"
        style={{ width: 240, paddingLeft: 30 }}
      />
    </div>
  );
}

const bar: CSSProperties = { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" };
const ikona: CSSProperties = {
  position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)",
  width: 15, height: 15, display: "inline-flex", color: "var(--ink-3)", pointerEvents: "none",
};
